import React, { FunctionComponent, useCallback } from 'react'
import styles from './NotificationElement.module.sass'

import { flow } from 'fp-ts/es6/function'
import {parseDate, toTimeString} from '../../../../utils/date-utils'
import ObjectName from '../../../etc/ObjectName/ObjectName'
import { HistoryData, HistoryResources, isResultHistory } from '../../../../responses/history'
import { UserIcon } from '../UserElement/UserElement'
import Ellipsis from '../../../etc/Ellipsis/Ellipsis'
import { ID } from '../../../../types/UtilityTypes'
import { urlWithParams, urlWithQuery } from '../../../../http/utils'
import { ROUTES } from '../../../../http/routes'
import ConditionLink from '../../../etc/ConditionLink'
import ChevronRightIcon from 'files/chevron/right.svg'
import { historyRenderer } from '../../../../utils/renderers'
import SideMark from '../../../etc/SideMark/SideMark'
import { Colors } from '../../../../styles'
import { pipe } from 'fp-ts/function'


type Props = {
    type: 'notification',
    notification: any, // eslint-disable-line
    resources: HistoryResources,
    onRead?: (notificationId: ID) => void,
}

const toTime = flow(parseDate, toTimeString)

const isNotDeleted = (data: HistoryData, resources: HistoryResources): boolean => {
    switch (data.type) {
        case 'project':
            return !resources.project[data.project].deleted_at
        case 'project_work_object':
            return !resources.project_work_object[data.project_work_object].deleted_at
        case 'task':
            return !resources.task[data.task].deleted_at
    }
}

export const historyDataToUrl = (data: HistoryData): string => {
    switch (data.type) {
        case 'project':
            return urlWithParams({ projectId: data.project })(ROUTES.PROJECT_DETAIL)
        case 'project_work_object':
            return urlWithParams({ projectId: data.project, objectId: data.project_work_object })(ROUTES.OBJECT)
        case 'task':
            return pipe(
                ROUTES.TASK,
                urlWithParams({ taskId: data.task }),
                urlWithQuery({ comment: data.owner_type === 'comment'? data.owner_id: undefined})
            )
    }
}

const NotificationOutboxElement: FunctionComponent<Props> = props => {
    const { notification: { id, read }, onRead } = props
    const onReadFn = useCallback(() => {
        !read && onRead?.(id)
    }, [id, read, onRead])

    const notification = props.notification
    const resources = props.resources


    return <ConditionLink
        condition={isNotDeleted(notification, resources)}
        to={historyDataToUrl(notification)}
    >
        <div className={styles.element} onMouseEnter={onReadFn}>
            <div className={styles.title}>
                <Ellipsis className={styles.project}>
                    {resources.project[props.notification.project].name}
                </Ellipsis>
                <div className={styles.time}>{toTime(props.notification.created_at)}</div>
                {
                    props.notification.type !== 'project' && <ObjectName
                        className={styles.object}
                        object={resources.project_work_object[props.notification.project_work_object]}
                    />
                }
                {
                    props.notification.type === 'task' && <div className={styles.task}>
                        <ChevronRightIcon className={styles.icon}/>
                        {resources.task[props.notification.task].type.name}
                    </div>
                }
                <UserIcon
                    className={styles.user}
                    user={resources.user[props.notification.user]}
                />
            </div>
            <SideMark color={
                !props.notification.read? Colors.orange:
                    isResultHistory(notification, resources)? Colors.green: undefined
            }/>
            {
                notification.actions.map(historyRenderer(resources, notification, false))
            }
        </div>
    </ConditionLink>
}

export default NotificationOutboxElement